import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { MailIcon, KeyIcon, LockIcon, EyeIcon, EyeOffIcon, ArrowLeftIcon } from "./AuthIcons";
import { authService, getAuthErrorMessage } from "../../services/authService";
import { validateEmail, validatePassword } from "../../utils/validators";
import Toast from "../../components/Common/Toast";

type Step = "email" | "otp" | "reset";

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const showError = (msg: string) => setToast({ type: "error", message: msg });
  const showSuccess = (msg: string) => setToast({ type: "success", message: msg });

  const stepIndex = step === "email" ? 0 : step === "otp" ? 1 : 2;

  /* Step 1 — request OTP */
  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      showError("Enter a valid email address");
      return;
    }
    try {
      setLoading(true);
      await authService.forgotPassword({ email: email.trim() });
      showSuccess("If an account exists, a 6-digit code has been sent to your email.");
      setStep("otp");
    } catch (err: any) {
      showError(getAuthErrorMessage(err, "Could not send the reset code"));
    } finally {
      setLoading(false);
    }
  };

  /* Step 2 — verify OTP */
  const submitOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      showError("Enter the 6-digit code");
      return;
    }
    try {
      setLoading(true);
      const data = await authService.verifyOtp({ email: email.trim(), otp });
      setResetToken(data.reset_token);
      showSuccess("Code verified. Set your new password.");
      setStep("reset");
    } catch (err: any) {
      showError(getAuthErrorMessage(err, "Invalid or expired code"));
    } finally {
      setLoading(false);
    }
  };

  /* Step 3 — set new password */
  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword(password)) {
      showError("Min 8 chars with uppercase, lowercase, number & special character");
      return;
    }
    if (password !== confirmPassword) {
      showError("Passwords do not match");
      return;
    }
    try {
      setLoading(true);
      await authService.resetPassword({ token: resetToken, new_password: password });
      navigate("/signin", {
        replace: true,
        state: { notice: "Password reset successful. Please sign in." },
      });
    } catch (err: any) {
      showError(getAuthErrorMessage(err, "Could not reset password"));
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    try {
      setLoading(true);
      await authService.forgotPassword({ email: email.trim() });
      showSuccess("A new code has been sent to your email.");
    } catch (err: any) {
      showError(getAuthErrorMessage(err, "Could not resend the code"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Link to="/signin" className="auth-back">
        <ArrowLeftIcon /> Back to sign in
      </Link>

      <div className="auth-steps">
        <span className={stepIndex >= 0 ? "active" : ""} />
        <span className={stepIndex >= 1 ? "active" : ""} />
        <span className={stepIndex >= 2 ? "active" : ""} />
      </div>

      <p className="auth-eyebrow">Account recovery</p>

      {/* STEP 1 — EMAIL */}
      {step === "email" && (
        <>
          <h2>Forgot password?</h2>
          <p className="auth-subtitle">
            Enter your registered email and we'll send you a code to reset it.
          </p>
          <form onSubmit={submitEmail} noValidate>
            <div className="auth-field">
              <label htmlFor="email">Email address</label>
              <div className="auth-input-wrap">
                <span className="auth-icon">
                  <MailIcon />
                </span>
                <input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
            </div>
            <button type="submit" className="auth-submit" disabled={loading}>
              {loading && <span className="auth-spinner" />}
              {loading ? "Sending..." : "Send reset code"}
            </button>
          </form>
        </>
      )}

      {/* STEP 2 — OTP */}
      {step === "otp" && (
        <>
          <h2>Enter the code</h2>
          <p className="auth-subtitle">
            We sent a 6-digit code to <strong>{email}</strong>.
          </p>
          <form onSubmit={submitOtp} noValidate>
            <div className="auth-field auth-otp">
              <label htmlFor="otp">Verification code</label>
              <div className="auth-input-wrap">
                <span className="auth-icon">
                  <KeyIcon />
                </span>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  autoFocus
                />
              </div>
            </div>
            <button type="submit" className="auth-submit" disabled={loading}>
              {loading && <span className="auth-spinner" />}
              {loading ? "Verifying..." : "Verify code"}
            </button>
          </form>
          <p className="auth-foot">
            Didn't receive it?{" "}
            <button type="button" onClick={resendOtp} disabled={loading}>
              Resend code
            </button>
          </p>
        </>
      )}

      {/* STEP 3 — RESET */}
      {step === "reset" && (
        <>
          <h2>Set a new password</h2>
          <p className="auth-subtitle">Choose a strong password for your account.</p>
          <form onSubmit={submitReset} noValidate>
            <div className="auth-field">
              <label htmlFor="newPassword">New password</label>
              <div className="auth-input-wrap">
                <span className="auth-icon">
                  <LockIcon />
                </span>
                <input
                  id="newPassword"
                  className="has-eye"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="auth-eye"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
            <div className="auth-field">
              <label htmlFor="confirmNewPassword">Confirm new password</label>
              <div className="auth-input-wrap">
                <span className="auth-icon">
                  <LockIcon />
                </span>
                <input
                  id="confirmNewPassword"
                  className="has-eye"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="auth-eye"
                  onClick={() => setShowConfirmPassword((s) => !s)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
            <button type="submit" className="auth-submit" disabled={loading}>
              {loading && <span className="auth-spinner" />}
              {loading ? "Saving..." : "Reset password"}
            </button>
          </form>
        </>
      )}

      {toast && (
        <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
